import { Navigate, Route, Routes, useLocation } from "react-router";

import { AppShell } from "@/components/app-shell";
import { AdminRoute, DeveloperRoute, ProtectedRoute } from "@/components/protected-route";
import {
  AnalyticsIndex,
  AnalyticsLayout,
  CallsPage,
  FailuresPage,
  IssuePage,
  SpeedPage,
} from "@/features/analytics";
import { AppsIndex, BezettingPage, MaandrapportPage } from "@/features/apps";
import { DocPage, DocsLayout, DocsStart } from "@/features/docs";
import { HomePage } from "@/features/home";
import { LoginPage } from "@/features/login";
import { rootRedirectTarget } from "@/features/login/utils";
import { NotFoundPage } from "@/features/not-found";
import { PeopleLayout, UsersPage } from "@/features/people";
import { RoadmapPage } from "@/features/roadmap";
import { SettingsLayout } from "@/features/settings";
import { AboutSettings } from "@/features/settings/about";
import { AccountSettings } from "@/features/settings/account";
import { ActivitySettings } from "@/features/settings/activity";
import { ViewAsSettings } from "@/features/settings/view-as";
import { TeamsPage } from "@/features/teams";
import { ToolsPage } from "@/features/tools";
import { DOC_PAGES } from "@/lib/docs-pages";
import { PEOPLE_PATH, TEAMS_PATH } from "@/lib/navigation";

export function App() {
  return (
    <Routes>
      <Route path="/" element={<RootRedirect />} />
      <Route path="/login" element={<LoginPage />} />
      {/* Every signed-in page shares one app shell, which stays mounted between them. */}
      <Route element={<ProtectedRoute />}>
        <Route element={<AppShell />}>
          <Route path="/home" element={<HomePage />} />
          <Route path="/tools" element={<ToolsPage />} />
          {/* The apps: each says "Geen toegang" itself to someone who may not open it. */}
          <Route path="/apps" element={<AppsIndex />} />
          <Route path="/apps/bezetting/:tab?" element={<BezettingPage />} />
          <Route path="/apps/maandrapport/:tab?/:client?" element={<MaandrapportPage />} />
          {/* The docs: a page per topic, with its own navigation; `/docs` opens the first. */}
          <Route path="/docs" element={<DocsLayout />}>
            <Route index element={<DocsStart />} />
            {DOC_PAGES.map((page) => (
              <Route key={page} path={page} element={<DocPage page={page} />} />
            ))}
          </Route>
          <Route element={<AdminRoute />}>
            {/* Mensen en teams: the dashboard's accounts and the connector's teams, as two tabs. */}
            <Route path={PEOPLE_PATH} element={<PeopleLayout />}>
              <Route index element={<UsersPage />} />
              <Route path="teams" element={<TeamsPage />} />
            </Route>
            <Route path="/roadmap" element={<RoadmapPage />} />
            {/* Statistieken: four parts under one title and a row of tabs; an issue under Fouten. */}
            <Route path="/analytics" element={<AnalyticsLayout />}>
              <Route index element={<AnalyticsIndex />} />
              <Route path="oproepen" element={<CallsPage />} />
              <Route path="fouten" element={<FailuresPage />} />
              <Route path="fouten/:key" element={<IssuePage />} />
              <Route path="snelheid" element={<SpeedPage />} />
            </Route>
          </Route>
          {/* Settings: its own navigation beside the open section. */}
          <Route path="/settings" element={<SettingsLayout />}>
            <Route index element={<MovedTo path="/settings/account" />} />
            <Route path="account" element={<AccountSettings />} />
            <Route path="about" element={<AboutSettings />} />
            <Route element={<AdminRoute />}>
              <Route path="activity" element={<ActivitySettings />} />
            </Route>
            {/* Bekijken als: the dashboard as a colleague or a role sees it, read-only. */}
            <Route element={<DeveloperRoute />}>
              <Route path="view-as" element={<ViewAsSettings />} />
            </Route>
          </Route>
        </Route>
      </Route>
      {/* Former addresses, kept for bookmarks. */}
      <Route path="/profile" element={<MovedTo path="/settings/account" />} />
      <Route path="/admin/connector" element={<MovedTo path="/analytics" />} />
      <Route path="/admin/users" element={<MovedTo path={PEOPLE_PATH} />} />
      <Route path="/admin/activity" element={<MovedTo path="/settings/activity" />} />
      <Route path="/settings/users" element={<MovedTo path={PEOPLE_PATH} />} />
      <Route path="/settings/teams" element={<MovedTo path={TEAMS_PATH} />} />
      <Route path="/teams" element={<MovedTo path={TEAMS_PATH} />} />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

/** `/` goes to `/home`, or to the login page when Supabase sent a sign-in result there. */
function RootRedirect() {
  const { search, hash } = useLocation();
  return <Navigate to={rootRedirectTarget(search, hash)} replace />;
}

/** A page that moved: on to its new path, keeping the query (`?days=30`) and the hash. */
function MovedTo({ path }: { path: string }) {
  const { search, hash } = useLocation();
  return <Navigate to={{ pathname: path, search, hash }} replace />;
}
